import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Input,
  MetricCard,
  PageHeader,
  Select,
  SkeletonRows,
  useToast,
} from "../../components/ui";
import {
  adminService,
  type AdminOverview,
  type OrganizationNode,
  type PlatformSettings,
} from "../../services/admin.service";
import { formatMoney, formatNumber } from "../../lib/format";

const tierLabels: Record<string, string> = {
  master: "Master",
  franqueado: "Franqueado",
  cliente: "Cliente",
};


function flatten(nodes: OrganizationNode[]): OrganizationNode[] {
  return nodes.flatMap((node) => [node, ...flatten(node.children)]);
}

/**
 * Painel administrativo. O conteúdo é o que o backend devolve para o escopo
 * do usuário: superadmin vê a plataforma, master/franqueado veem sua estrutura.
 */
export default function AdministrationPage() {
  const toast = useToast();
  const [overview, setOverview] = useState<AdminOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [settings, setSettings] = useState<PlatformSettings | null>(null);
  const [savingSettings, setSavingSettings] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminService.overview();
      setOverview(data);
      setError(null);
      if (data.scope.can_manage_platform) {
        setSettings(await adminService.settings());
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function saveSettings() {
    if (!settings) return;
    setSavingSettings(true);
    try {
      setSettings(await adminService.updateSettings(settings));
      toast.success("Configurações globais salvas.");
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setSavingSettings(false);
    }
  }

  const metrics = overview?.metrics;
  const rows = overview ? flatten(overview.organizations) : [];

  const areas = [
    { to: "/admin/organizations", title: "Organizações", text: "Hierarquia e escopos." },
    { to: "/admin/users", title: "Usuários", text: "Contas, papéis e vínculos." },
    { to: "/admin/plans", title: "Planos", text: "Preços, limites e revenda." },
    { to: "/admin/ai-providers", title: "Providers IA", text: "Modelos e credenciais." },
  ];

  return (
    <>
      <PageHeader
        title="Administração"
        description={
          overview
            ? `Visão de ${overview.scope.role_label} — ${formatNumber(overview.scope.organizations)} organização(ões) no seu escopo.`
            : "Carregando escopo…"
        }
        actions={
          <Button variant="secondary" onClick={() => void load()}>
            Atualizar
          </Button>
        }
      />

      {error ? (
        <Card>
          <EmptyState title="Não foi possível carregar" description={error} />
        </Card>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {overview?.scope.can_see_revenue && (
              <MetricCard
                label="MRR"
                loading={loading}
                value={metrics?.mrr_cents != null ? formatMoney(metrics.mrr_cents) : "—"}
                hint="Assinaturas ativas, mensalizadas"
              />
            )}
            <MetricCard
              label="Masters"
              loading={loading}
              value={formatNumber(metrics?.masters ?? 0)}
            />
            <MetricCard
              label="Franqueados"
              loading={loading}
              value={formatNumber(metrics?.franqueados ?? 0)}
            />
            <MetricCard
              label="Clientes"
              loading={loading}
              value={formatNumber(metrics?.clientes ?? 0)}
            />
            <MetricCard label="Usuários" loading={loading} value={formatNumber(metrics?.users ?? 0)} />
            <MetricCard
              label="Projetos"
              loading={loading}
              value={formatNumber(metrics?.projects ?? 0)}
            />
            <MetricCard
              label="Execuções IA (30d)"
              loading={loading}
              value={formatNumber(metrics?.ai_runs_30d ?? 0)}
              hint={`${formatNumber(metrics?.ai_runs_failed_30d ?? 0)} com falha`}
            />
            <MetricCard
              label="Consumo (30d)"
              loading={loading}
              value={formatNumber(Math.round(metrics?.usage_30d ?? 0))}
            />
            <MetricCard
              label="Deploys (30d)"
              loading={loading}
              value={formatNumber(metrics?.deployments_30d ?? 0)}
            />
            <MetricCard
              label="Providers IA"
              loading={loading}
              value={`${formatNumber(metrics?.providers_healthy ?? 0)}/${formatNumber(metrics?.providers_total ?? 0)}`}
              hint="Conexões testadas com sucesso"
            />
            <MetricCard
              label="Assinaturas ativas"
              loading={loading}
              value={formatNumber(metrics?.subscriptions_active ?? 0)}
              hint={`${formatNumber(metrics?.subscriptions_trial ?? 0)} em teste`}
            />
          </div>

          <Card className="mt-6">
            <h2 className="mb-3 text-sm font-medium text-ink">Estrutura</h2>
            {loading ? (
              <SkeletonRows rows={4} />
            ) : rows.length === 0 ? (
              <EmptyState title="Nenhuma organização" description="Sua estrutura ainda está vazia." />
            ) : (
              <ul className="space-y-1">
                {rows.map((node) => (
                  <li
                    key={node.id}
                    className="flex flex-wrap items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-line/20"
                    style={{ paddingLeft: `${8 + node.depth * 18}px` }}
                  >
                    <span className="text-ink">{node.name}</span>
                    <Badge>{tierLabels[node.tier] ?? node.tier}</Badge>
                    <Badge tone={node.status === "active" ? "ok" : "warn"}>{node.status}</Badge>
                    <span className="text-xs text-ink-muted">
                      {formatNumber(node.projects_count)} projetos ·{" "}
                      {formatNumber(node.members_count)} usuários
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {areas.map((area) => (
              <Link key={area.to} to={area.to}>
                <Card className="transition-colors hover:border-brand/50">
                  <p className="text-sm font-medium text-ink">{area.title}</p>
                  <p className="mt-1 text-sm text-ink-muted">{area.text}</p>
                </Card>
              </Link>
            ))}
          </div>

          {settings && (
            <Card className="mt-6">
              <h2 className="mb-3 text-sm font-medium text-ink">Configurações globais</h2>
              <div className="grid gap-3 sm:grid-cols-2">
                <Input
                  label="Nome da plataforma"
                  value={settings.platform_name ?? ""}
                  onChange={(e) => setSettings({ ...settings, platform_name: e.target.value })}
                />
                <Input
                  label="E-mail de suporte"
                  type="email"
                  value={settings.support_email ?? ""}
                  onChange={(e) => setSettings({ ...settings, support_email: e.target.value })}
                />
                <Select
                  label="Cadastro aberto"
                  value={settings.signup_enabled ? "1" : "0"}
                  onChange={(e) => setSettings({ ...settings, signup_enabled: e.target.value === "1" })}
                  options={[
                    { value: "0", label: "Somente por convite" },
                    { value: "1", label: "Aberto" },
                  ]}
                />
                <Input
                  label="Plano padrão (slug)"
                  value={settings.default_plan_slug ?? ""}
                  onChange={(e) => setSettings({ ...settings, default_plan_slug: e.target.value })}
                />
                <Input
                  label="Limite padrão de projetos"
                  type="number"
                  value={settings.default_project_limit ?? 0}
                  onChange={(e) =>
                    setSettings({ ...settings, default_project_limit: Number(e.target.value) })
                  }
                />
                <Input
                  label="Limite padrão de execuções IA"
                  type="number"
                  value={settings.default_ai_run_limit ?? 0}
                  onChange={(e) =>
                    setSettings({ ...settings, default_ai_run_limit: Number(e.target.value) })
                  }
                />
                <Input
                  label="Limite padrão de deploys"
                  type="number"
                  value={settings.default_deploy_limit ?? 0}
                  onChange={(e) =>
                    setSettings({ ...settings, default_deploy_limit: Number(e.target.value) })
                  }
                />
                <Input
                  label="Armazenamento padrão (MB)"
                  type="number"
                  value={settings.default_storage_mb_limit ?? 0}
                  onChange={(e) =>
                    setSettings({ ...settings, default_storage_mb_limit: Number(e.target.value) })
                  }
                />
                <Input
                  label="Máximo de sandboxes simultâneas"
                  type="number"
                  value={settings.sandbox_max_containers ?? 0}
                  onChange={(e) =>
                    setSettings({ ...settings, sandbox_max_containers: Number(e.target.value) })
                  }
                />
              </div>
              <div className="mt-4 flex justify-end">
                <Button onClick={() => void saveSettings()} loading={savingSettings}>
                  Salvar configurações
                </Button>
              </div>
            </Card>
          )}
        </>
      )}
    </>
  );
}
